import { Box } from '@mui/material';
import Konva from 'konva';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Image as KonvaImage, Layer, Stage } from 'react-konva';
import useImage from 'use-image';
import { useAppSelector } from '../../app/hooks';
import { PointDetection } from '../../services/data/impl/wrangler/schemas/pointDetections';
import { ImageScaleParams, ImageURLType } from './ImageController';
import {
  selectDepthMapOpacity,
  selectFrontendColorCorrectionResult,
  selectImageBrightness,
  selectShowDepthMap,
  selectShowMasks,
} from './imageSlice';
import MaskLayers from './MaskLayers';
import MaskTooltip from './MaskTooltip';
import { useImageContext } from './providers/ImageProvider';
import { colorizeDepthMap } from './utils/imageUtils';

interface Props {
  isVisible: boolean;
  finalImage: HTMLImageElement | null | undefined;
  widthParent: number;
  heightParent: number;
  detectionsFiltered: PointDetection[] | undefined;
  isDetectionsLoadingOrFetching: boolean;
  imageUrls: ImageURLType;
}

export type MaskTooltipInfo = {
  visible: boolean;
  mousePosX: number;
  mousePosY: number;
  feature: string | null;
  model: string | null;
  score: number | '' | null | undefined;
  distance: number | '' | null | undefined;
  className: string | null;
  modelVersion: string | null;
};

const initialMaskTooltipInfo: MaskTooltipInfo = {
  visible: false,
  mousePosX: 0,
  mousePosY: 0,
  feature: null,
  score: null,
  distance: null,
  model: null,
  className: null,
  modelVersion: null,
};

const ImageStage: React.FC<Props> = ({
  isVisible,
  finalImage,
  widthParent,
  heightParent,
  detectionsFiltered,
  isDetectionsLoadingOrFetching,
  imageUrls,
}) => {
  const imageContext = useImageContext();
  const stageRef = useRef<Konva.Stage>(null);
  const imageRef = useRef<Konva.Image>(null);
  const brightness = useAppSelector(selectImageBrightness);
  const showDepthMap = useAppSelector(selectShowDepthMap);
  const depthMapOpacity = useAppSelector(selectDepthMapOpacity);
  const showMasks = useAppSelector(selectShowMasks);
  const frontendColorCorrectionResult = useAppSelector(
    selectFrontendColorCorrectionResult
  );
  const [maskTooltipInfo, setMaskTooltipInfo] = useState<MaskTooltipInfo>(
    initialMaskTooltipInfo
  );

  const [dimensions, setDimensions] = useState({
    width: widthParent,
    height: heightParent,
  });

  const [imageScaleParams, setImageScaleParams] = useState<ImageScaleParams>({
    x: 0,
    y: 0,
    scale: 1,
  });

  useEffect(() => {
    setDimensions({ width: widthParent, height: heightParent });
  }, [widthParent, heightParent]);

  // Compute scaling for final image
  useEffect(() => {
    if (finalImage) {
      const scaleX = dimensions.width / finalImage.width;
      const scaleY = dimensions.height / finalImage.height;
      const scale = Math.min(scaleX, scaleY);
      setImageScaleParams({ x: 0, y: 0, scale });
    }
  }, [finalImage, dimensions]);

  // Update stageRef and container width in image context
  useEffect(() => {
    if (imageContext?.setStageRef) {
      imageContext.setStageRef(stageRef.current);
    }
    if (imageContext?.setImageContainerWidth) {
      imageContext.setImageContainerWidth(widthParent);
    }
  }, [stageRef.current, widthParent]);

  // Apply brightness
  useEffect(() => {
    /**
     * Apply brightness to the Konva Image node.
     * @param value - The brightness value.
     */
    const adjustBrightness = (value: number) => {
      if (imageRef.current) {
        imageRef.current.brightness(value / 100);
        imageRef.current.cache();
        imageRef.current.getLayer()?.batchDraw();
      }
    };

    if (finalImage) {
      adjustBrightness(brightness);
      // Apply brightness again as backup in case the first time is not successful.
      // React-Konva doesn't have an onload callback so in some cases we need a slight delay
      setTimeout(() => {
        adjustBrightness(brightness);
      }, 20);
    }
  }, [brightness, finalImage, frontendColorCorrectionResult.isLoading]);

  // Fetch depth map image conditionally
  const [depthMapImage] = useImage(
    showDepthMap ? imageUrls?.depthMapUrl || '' : '',
    'anonymous'
  );

  // Colorize depth map on frontend
  const coloredDepthMap = useMemo(() => {
    if (showDepthMap && imageUrls?.depthMapUrl && depthMapImage) {
      return colorizeDepthMap(depthMapImage);
    }
    return null;
  }, [showDepthMap, imageUrls?.depthMapUrl, depthMapImage]);

  if (!finalImage) {
    return null;
  }

  return (
    <Box
      sx={{
        width: finalImage.width * imageScaleParams.scale,
        height: finalImage.height * imageScaleParams.scale,
        display: isVisible ? 'flex' : 'none',
        position: 'absolute',
        backgroundColor: (theme) => theme.colors.darkTheme.grey.light,
      }}
    >
      <Stage
        width={finalImage.width * imageScaleParams.scale}
        height={finalImage.height * imageScaleParams.scale}
        ref={stageRef}
      >
        <Layer>
          <KonvaImage
            ref={imageRef}
            x={imageScaleParams.x}
            y={imageScaleParams.y}
            image={finalImage}
            scaleX={imageScaleParams.scale}
            scaleY={imageScaleParams.scale}
            filters={[Konva.Filters.Brighten]}
          />
        </Layer>
        {showDepthMap && coloredDepthMap && (
          <Layer>
            <KonvaImage
              x={imageScaleParams.x}
              y={imageScaleParams.y}
              image={coloredDepthMap}
              scaleX={imageScaleParams.scale}
              scaleY={imageScaleParams.scale}
              opacity={depthMapOpacity / 100}
            />
          </Layer>
        )}
        {showMasks && (
          <>
            <MaskLayers
              key={'MaskLayers'}
              pointCubeDetections={
                detectionsFiltered && !isDetectionsLoadingOrFetching
                  ? detectionsFiltered
                  : null
              }
              imageScaleParams={imageScaleParams}
              imageWidth={finalImage.width}
              imageHeight={finalImage.height}
              setMaskTooltipInfo={setMaskTooltipInfo}
            />
            <Layer>
              <MaskTooltip
                maskTooltipInfo={maskTooltipInfo}
                widthParent={finalImage.width * imageScaleParams.x}
                heightParent={finalImage.height * imageScaleParams.y}
              />
            </Layer>
          </>
        )}
      </Stage>
    </Box>
  );
};

export default ImageStage;
